import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  FileText,
  CheckCircle,
  Clock,
  Download,
  Loader2,
  Shield,
  Lock,
  PenTool,
  AlertCircle,
  Building2,
  Calendar,
  DollarSign,
  Hash,
  Globe,
  Monitor,
  MapPin,
  Trash2,
  Upload,
  Image as ImageIcon,
  ShieldCheck,
  FileKey
} from 'lucide-react';
import CertSignaturePanel from '../components/CertSignaturePanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_rapid-systems/artifacts/cv8q3bmw_LOGO%20ANALYTICA.png";

const FirmarContratoPage = () => {
  const { token } = useParams();
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [contract, setContract] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSigning, setIsSigning] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signatureMode, setSignatureMode] = useState('draw'); // 'draw' or 'upload'
  const [uploadedSignature, setUploadedSignature] = useState(null);
  const [clientLocation, setClientLocation] = useState(null);
  const [showContract, setShowContract] = useState(false);
  // Firma con certificado PAdES
  const [signatureType, setSignatureType] = useState('simple'); // 'simple' | 'cert'
  const [certData, setCertData] = useState(null);
  const [signingWithCert, setSigningWithCert] = useState(false);

  useEffect(() => {
    fetchContract();
    getClientLocation();
  }, [token]);

  useEffect(() => {
    // Allow signing for 'sent' or 'draft' status (new model uses 'sent', old uses 'generated')
    const canSign = contract?.status === 'generated' || contract?.status === 'sent' || contract?.status === 'draft';
    if (canvasRef.current && canSign && signatureMode === 'draw') {
      initCanvas();
    }
  }, [contract, signatureMode]);

  const fetchContract = async () => {
    try {
      const response = await axios.get(`${API}/contracts/token/${token}`);
      setContract(response.data);
    } catch (error) {
      console.error('Error fetching contract:', error);
      toast.error('Contrato no encontrado');
    } finally {
      setIsLoading(false);
    }
  };

  const getClientLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setClientLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        () => {
          setClientLocation({ latitude: 'No disponible', longitude: 'No disponible' });
        }
      );
    }
  };

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#2F3D8E';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  const startDrawing = (e) => {
    const canSign = contract?.status === 'generated' || contract?.status === 'sent' || contract?.status === 'draft';
    if (!canSign || signatureMode !== 'draw') return;
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    const canSign = contract?.status === 'generated' || contract?.status === 'sent' || contract?.status === 'draft';
    if (!isDrawing || !canSign || signatureMode !== 'draw') return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    if (signatureMode === 'draw') {
      initCanvas();
    } else {
      setUploadedSignature(null);
    }
    setHasSignature(false);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor seleccione una imagen válida');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe superar 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadedSignature(event.target.result);
      setHasSignature(true);
    };
    reader.readAsDataURL(file);
  };

  const handleSign = async () => {
    if (!hasSignature) {
      toast.error('Por favor, proporcione su firma');
      return;
    }

    setIsSigning(true);
    try {
      let signatureImage;
      
      if (signatureMode === 'draw') {
        const canvas = canvasRef.current;
        signatureImage = canvas.toDataURL('image/png');
      } else {
        signatureImage = uploadedSignature;
      }
      
      const deviceInfo = navigator.userAgent;
      const locationStr = clientLocation 
        ? `Lat: ${clientLocation.latitude}, Lng: ${clientLocation.longitude}`
        : 'No disponible';

      const response = await axios.post(`${API}/contracts/${contract.id}/sign-client`, {
        signature_image: signatureImage,
        signer_name: contract.client_representative_name || contract.client_name || 'Cliente',
        location: locationStr,
        device_info: deviceInfo
      });

      toast.success('¡Contrato firmado exitosamente!');
      setContract(response.data.contract);
    } catch (error) {
      console.error('Error signing contract:', error);
      toast.error('Error al firmar el contrato');
    } finally {
      setIsSigning(false);
    }
  };

  const handleSignWithCert = async () => {
    if (!certData) { toast.error('Cargue y valide su certificado primero'); return; }
    setSigningWithCert(true);
    try {
      // Fetch contract PDF (the borrador/draft) as blob and convert to base64
      const r1 = await axios.get(`${API}/contracts/token/${token}/download-pdf`, { responseType: 'blob' });
      const blob = r1.data;
      const pdfBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result;
          resolve(result.includes(',') ? result.split(',', 2)[1] : result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const r = await axios.post(`${API}/signatures/contract/token/${token}/sign-with-cert`, {
        pdf_base64: pdfBase64,
        p12_base64: certData.p12_base64,
        password: certData.password,
        client_name: certData.info?.common_name,
        reason: 'Aceptacion del contrato',
        location: 'Ecuador',
      });
      toast.success(`Contrato firmado con certificado · ${r.data.cert_info?.common_name}`);
      fetchContract();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al firmar con certificado');
    } finally {
      setSigningWithCert(false);
    }
  };

  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    try {
      const response = await axios.get(`${API}/contracts/token/${token}/download-pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const filename = contract.status === 'completed' 
        ? `contrato_${contract.contract_number}.pdf`
        : `contrato_${contract.contract_number}_BORRADOR.pdf`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descargado exitosamente');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Error al descargar el PDF');
    } finally {
      setIsDownloading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-EC', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-[#2F3D8E]" />
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800">Contrato no encontrado</h1>
          <p className="text-gray-600 mt-2">El enlace puede haber expirado o ser inválido.</p>
        </div>
      </div>
    );
  }

  const getStatusInfo = () => {
    switch (contract.status) {
      case 'draft':
      case 'generated':
      case 'sent':
        return { icon: FileText, color: 'blue', text: 'Pendiente de Firma', description: 'El contrato está listo para ser firmado' };
      case 'client_signed':
        return { icon: Clock, color: 'yellow', text: 'Firmado - Pendiente Firma Analytica', description: 'Su firma fue recibida. Esperando firma de Analytica para completar el proceso.' };
      case 'completed':
        return { icon: CheckCircle, color: 'green', text: 'Completado', description: 'El contrato ha sido completado exitosamente' };
      default:
        return { icon: FileText, color: 'gray', text: 'Desconocido', description: '' };
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="Analytica" className="h-12" />
          <div className="flex items-center gap-2 text-gray-600">
            <Shield className="w-5 h-5 text-[#4C8C3B]" />
            <span className="text-sm font-medium">Firma Electrónica Segura</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Contract Number & Status */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <p className="text-sm text-gray-500">Contrato</p>
              <h1 className="text-2xl font-bold text-[#2F3D8E]">{contract.contract_number}</h1>
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full bg-${statusInfo.color}-100`}>
              <StatusIcon className={`w-5 h-5 text-${statusInfo.color}-600`} />
              <span className={`font-medium text-${statusInfo.color}-700`}>{statusInfo.text}</span>
            </div>
          </div>
          <p className="text-gray-600 mt-2">{statusInfo.description}</p>
        </div>

        {/* Progress Steps */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-bold text-[#2F3D8E] mb-4">Estado del Proceso</h2>
          <div className="flex items-center justify-between">
            {['Enviado', 'Firmado Cliente', 'Completado'].map((step, index) => {
              const statusOrder = { 'draft': 0, 'sent': 0, 'generated': 0, 'client_signed': 1, 'completed': 2 };
              const currentStatusIdx = statusOrder[contract.status] ?? 0;
              const isActive = index <= currentStatusIdx;
              const isCompleted = index < currentStatusIdx;
              
              return (
                <React.Fragment key={step}>
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isCompleted ? 'bg-[#4C8C3B] text-white' :
                      isActive ? 'bg-[#5DA5D8] text-white' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {isCompleted ? <CheckCircle className="w-5 h-5" /> : index + 1}
                    </div>
                    <span className={`text-xs mt-2 ${isActive ? 'text-[#2F3D8E] font-medium' : 'text-gray-500'}`}>
                      {step}
                    </span>
                  </div>
                  {index < 2 && (
                    <div className={`flex-1 h-1 mx-2 ${
                      isCompleted ? 'bg-[#4C8C3B]' : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Contract Summary */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-bold text-[#2F3D8E] mb-4">Resumen del Contrato</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Cliente</p>
                  <p className="font-medium">{contract.client_company_name || contract.client_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Hash className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">RUC</p>
                  <p className="font-medium">{contract.client_ruc}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Software</p>
                  <p className="font-medium">{contract.software_name}</p>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Valor Total</p>
                  <p className="font-bold text-[#4C8C3B] text-xl">{formatCurrency(contract.total_price)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Fecha de Creación</p>
                  <p className="font-medium">{new Date(contract.created_at).toLocaleDateString('es-EC')}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowContract(!showContract)}
            >
              {showContract ? 'Ocultar' : 'Ver'} Contrato Completo
            </Button>
            <Button
              variant="outline"
              onClick={handleDownloadPDF}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Descargar PDF {contract.status !== 'completed' && '(Borrador)'}
            </Button>
          </div>

          {showContract && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg max-h-96 overflow-y-auto text-sm">
              <h3 className="font-bold mb-2">CONTRATO DE PRESTACION DE DESARROLLO DE SOFTWARE EN LA NUBE (SaaS)</h3>
              <p className="mb-2">
                Comparecen, por una parte, la <strong>COMPANIA {contract.client_company_name || contract.client_name}</strong>, 
                legalmente representada por {contract.client_representative_title} <strong>{contract.client_representative_name}</strong>, 
                portador del RUC <strong>{contract.client_ruc}</strong>, con domicilio en <strong>{contract.client_address}</strong>, 
                quien en adelante se denominará LA COMPANIA; y, por otra parte, <strong>ANALYTICA</strong>, representada por 
                {contract.analytica_representative || 'Ing. CESAR ALEJANDRO AMAYA HERNANDEZ'}, 
                quien en adelante se denominará EL DESARROLLADOR.
              </p>
              <p className="mb-2">
                <strong>OBJETO:</strong> Prestación del servicio de acceso remoto del software denominado <strong>{contract.software_name}</strong>.
              </p>
              <p className="mb-2">
                <strong>PRECIO TOTAL:</strong> {formatCurrency(contract.total_price)}
              </p>
              {contract.payment_phases?.length > 0 && (
                <div className="mb-2">
                  <strong>FORMA DE PAGO:</strong>
                  <ul className="list-disc list-inside ml-4 mt-1">
                    {contract.payment_phases.map((phase, i) => (
                      <li key={i}>{phase.description}: {formatCurrency(phase.amount)} (Semana {phase.week})</li>
                    ))}
                  </ul>
                </div>
              )}
              {contract.total_weeks && (
                <p className="mb-2">
                  <strong>PLAZO DE IMPLEMENTACION:</strong> {contract.total_weeks} semanas
                </p>
              )}
              <p className="text-xs text-gray-500 mt-4">
                * Este es un resumen. El contrato completo incluye todas las cláusulas sobre propiedad intelectual, 
                confidencialidad, protección de datos, seguridad, y demás términos legales.
              </p>
            </div>
          )}
        </div>

        {/* Signature Section */}
        {(contract.status === 'generated' || contract.status === 'sent' || contract.status === 'draft') && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-lg font-bold text-[#2F3D8E] mb-4 flex items-center gap-2">
              <PenTool className="w-5 h-5" />
              Firmar Contrato
            </h2>

            {/* Toggle tipo de firma */}
            <div className="flex gap-2 mb-4 border-b">
              <button onClick={() => setSignatureType('simple')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${signatureType === 'simple' ? 'border-[#2F3D8E] text-[#2F3D8E]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                      data-testid="tab-firma-simple">
                Firma Electronica Simple
              </button>
              <button onClick={() => setSignatureType('cert')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${signatureType === 'cert' ? 'border-[#2F3D8E] text-[#2F3D8E]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                      data-testid="tab-firma-cert">
                Firma con Certificado (PAdES) <span className="ml-1 text-[10px] bg-[#4C8C3B] text-white px-1.5 py-0.5 rounded-full">avanzada</span>
              </button>
            </div>

            {signatureType === 'cert' ? (
              <>
                <p className="text-xs text-gray-600 mb-3">
                  Suba su certificado digital (.p12/.pfx) emitido por entidad autorizada — BCE (Ecuador), SAT (Mexico), RENIEC (Peru), etc.
                  La firma se incrustara criptograficamente en el PDF (PAdES) cumpliendo Ley de Comercio Electronico EC, Ley 527 CO, Ley 19.799 CL, Ley 25.506 AR.
                </p>
                <CertSignaturePanel
                  onReady={(p12_base64, password, info) => setCertData({ p12_base64, password, info })}
                  onClear={() => setCertData(null)}
                />
                <Button className="bg-[#4C8C3B] hover:bg-[#3d7030] w-full mt-4" onClick={handleSignWithCert}
                        disabled={!certData || signingWithCert} data-testid="sign-contract-cert-btn">
                  {signingWithCert ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Firmando PAdES...</> :
                                     <><ShieldCheck className="w-4 h-4 mr-2" /> Firmar contrato con certificado</>}
                </Button>
              </>
            ) : (
              <>
            {/* Signature Mode Selector */}
            <div className="flex gap-2 mb-4">
              <Button
                variant={signatureMode === 'draw' ? 'default' : 'outline'}
                onClick={() => { setSignatureMode('draw'); clearSignature(); }}
                className={signatureMode === 'draw' ? 'bg-[#2F3D8E]' : ''}
              >
                <PenTool className="w-4 h-4 mr-2" />
                Dibujar Firma
              </Button>
              <Button
                variant={signatureMode === 'upload' ? 'default' : 'outline'}
                onClick={() => { setSignatureMode('upload'); clearSignature(); }}
                className={signatureMode === 'upload' ? 'bg-[#2F3D8E]' : ''}
              >
                <Upload className="w-4 h-4 mr-2" />
                Subir Imagen
              </Button>
            </div>

            {signatureMode === 'draw' ? (
              <>
                <p className="text-gray-600 mb-4">
                  Dibuje su firma en el recuadro a continuación. Al firmar, acepta los términos y condiciones del contrato.
                </p>
                
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-2 mb-4">
                  <canvas
                    ref={canvasRef}
                    width={600}
                    height={200}
                    className="w-full bg-white rounded-lg cursor-crosshair touch-none"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                </div>
              </>
            ) : (
              <>
                <p className="text-gray-600 mb-4">
                  Suba una imagen de su firma (PNG, JPG, máximo 5MB). Al firmar, acepta los términos y condiciones del contrato.
                </p>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/*"
                  className="hidden"
                />
                
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-xl p-6 mb-4 text-center cursor-pointer hover:border-[#2F3D8E] transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploadedSignature ? (
                    <div className="space-y-2">
                      <img 
                        src={uploadedSignature} 
                        alt="Firma subida" 
                        className="max-h-32 mx-auto rounded-lg border"
                      />
                      <p className="text-sm text-green-600">Imagen de firma cargada</p>
                    </div>
                  ) : (
                    <div className="space-y-2 text-gray-500">
                      <ImageIcon className="w-12 h-12 mx-auto" />
                      <p>Haga clic para seleccionar una imagen</p>
                      <p className="text-xs">PNG, JPG (máx. 5MB)</p>
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="flex gap-4 mb-6">
              <Button variant="outline" onClick={clearSignature} disabled={!hasSignature}>
                <Trash2 className="w-4 h-4 mr-2" />
                Limpiar
              </Button>
              <Button
                className="bg-[#4C8C3B] hover:bg-[#3d7030] flex-1"
                onClick={handleSign}
                disabled={!hasSignature || isSigning}
                data-testid="sign-contract-btn"
              >
                {isSigning ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Firmando...</>
                ) : (
                  <><PenTool className="w-4 h-4 mr-2" /> Firmar Contrato</>
                )}
              </Button>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
              <p className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4" />
                Su firma será registrada de forma segura junto con:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Fecha y hora de firma</li>
                <li>Dirección IP</li>
                <li>Ubicación geográfica (si autoriza)</li>
                <li>Información del dispositivo</li>
              </ul>
            </div>
              </>
            )}
          </div>
        )}

        {/* Signed Info */}
        {contract.client_signed_at && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-6">
            <h2 className="text-lg font-bold text-green-700 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Contrato Firmado
            </h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-green-600" />
                <span>Fecha: {new Date(contract.client_signed_at).toLocaleString('es-EC')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-green-600" />
                <span>IP: {contract.client_ip}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-green-600" />
                <span>Ubicación: {contract.client_location}</span>
              </div>
              <div className="flex items-center gap-2">
                <Monitor className="w-4 h-4 text-green-600" />
                <span className="truncate">Dispositivo: {contract.client_device?.substring(0, 50)}...</span>
              </div>
            </div>
            {contract.client_signature && (
              <div className="mt-4 p-4 bg-white rounded-lg">
                <p className="text-xs text-gray-500 mb-2">Firma Digital:</p>
                <img src={contract.client_signature} alt="Firma" className="max-h-24" />
              </div>
            )}
            
            {/* Download button for signed contracts */}
            <div className="mt-4">
              <Button
                onClick={handleDownloadPDF}
                disabled={isDownloading}
                className="bg-green-600 hover:bg-green-700"
              >
                {isDownloading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Descargar Contrato PDF {contract.status !== 'completed' && '(Borrador con marca de agua)'}
              </Button>
            </div>
          </div>
        )}

        {/* Certificate & Download Section */}
        {contract.status === 'completed' && (
          <div className="bg-[#2F3D8E] text-white rounded-2xl p-6 mb-6">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Certificado Digital
            </h2>
            <div className="bg-white/10 rounded-lg p-4 mb-4">
              <p className="text-sm opacity-80">Número de Certificado</p>
              <p className="text-xl font-mono font-bold">{contract.certificate_number}</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4 mb-4">
              <p className="text-sm opacity-80">Hash de Verificación (Blockchain)</p>
              <p className="text-xs font-mono break-all">{contract.certificate_hash}</p>
            </div>
            <Button 
              className="w-full bg-[#4C8C3B] hover:bg-[#3d7030]"
              onClick={handleDownloadPDF}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Descargar Contrato y Certificado (PDF)
            </Button>
          </div>
        )}

        {/* Security Footer */}
        <div className="text-center text-sm text-gray-500">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="w-4 h-4" />
            <span>Documento protegido con firma electrónica</span>
          </div>
          <p>© {new Date().getFullYear()} Analytica - Todos los derechos reservados</p>
        </div>
      </main>
    </div>
  );
};

export default FirmarContratoPage;
